import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Fragment } from 'react';
import { JsonView } from './JsonView';
import { planLabel } from './PlanProvenance';
import { BREAK_BY_TYPES_PLUGINS, BreakTypeRow, METRIC_ROWS, Metric, ReportDay, ReportShift, buildReportDay, buildReportShift, fmtDuration, fmtRequestInstant, fmtWeeklyOvertimePercent, metricLabel } from './reportData';
import { ShiftTimeline } from './ShiftTimeline';
import { daysInRange, weekdayName } from './rangeUtils';
import { AttributedRequestRow, DateRange, DayRun, RecalculateResponse, ShiftRun } from './types';

const SourcesStrip = ({ response }: { response: RecalculateResponse }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mb: 1 }}>
    <Chip size="small" color="primary" variant="outlined" label={planLabel(response.metadata)} />
    {Object.entries(response.metadata.sources)
      .filter(([, count]) => count > 0)
      .map(([kind, count]) => (
        <Chip key={kind} size="small" label={`${kind}: ${count}`} />
      ))}
  </Box>
);

/** The minutes metrics that ran, one row per family (planned / expected / worked / breaks / missed / paid). A value of zero is shown; a missing cell means the plugin did not run. */
const MetricsRow = ({ metrics }: { metrics: Metric[] }) => {
  const byLabel = new Map(metrics.map((m) => [m.label, m.minutes]));
  const rows = METRIC_ROWS.map((group) => ({ row: group.row, labels: group.fields.map(metricLabel).filter((label) => byLabel.has(label)) })).filter((group) => group.labels.length > 0);
  if (rows.length === 0) return null;
  return (
    <Box data-testid="metrics-row" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 1.5, rowGap: 0.25, mt: 0.5, alignItems: 'baseline' }}>
      {rows.map((group) => (
        <Fragment key={group.row}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {group.row}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25 }}>
            {group.labels.map((label) => (
              <Typography key={label} variant="caption" title={`${byLabel.get(label)} min`} sx={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                {label} <b>{fmtDuration(byLabel.get(label)!)}</b>
              </Typography>
            ))}
          </Box>
        </Fragment>
      ))}
    </Box>
  );
};

/**
 * The day-grain plugins' outputs for one date, one line per plugin that produced a chip — the plugin id on the left, its chips
 * on the right, like the metrics row of a shift — so a reader always sees which plugin a number came from. Under the lines: the
 * it_daily_overtime per-tier table, the it_weekly_overtime per-tag table, the it_hours_bank deposits table, and the raw outputs.
 */
const DayRow = ({ day }: { day: DayRun }) => {
  const report: ReportDay = buildReportDay(day);
  if (report.error) return <Alert severity="error" sx={{ mb: 1 }}>{`day: ${report.error}`}</Alert>;
  return (
    <Box data-testid="day-row" sx={{ mb: 1.5 }}>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
        day
      </Typography>
      {report.groups.length > 0 && (
        <Box data-testid="day-groups" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 1.5, rowGap: 0.25, mt: 0.5, alignItems: 'baseline' }}>
          {report.groups.map((group) => (
            <Fragment key={group.pluginId}>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {group.pluginId}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25 }}>
                {group.chips.map((chip) => (
                  <Typography key={chip.label} variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {chip.label} <b>{chip.value}</b>
                  </Typography>
                ))}
                {group.pluginId === 'it_rest_day_worked' && report.restDayStretches.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {report.restDayStretches.join(', ')}
                  </Typography>
                )}
              </Box>
            </Fragment>
          ))}
        </Box>
      )}
      {report.overtimeTiers.length > 0 && (
        <Table size="small" data-testid="daily-overtime" sx={{ mt: 1, mb: 0.5, '& td, & th': { fontFamily: 'monospace', fontSize: 12, py: 0.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell>it_daily_overtime tier</TableCell>
              <TableCell align="right">percent</TableCell>
              <TableCell align="right">minutes</TableCell>
              <TableCell>time ranges</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.overtimeTiers.map((tier) => (
              <TableRow key={tier.tier}>
                <TableCell>{tier.tier}</TableCell>
                <TableCell align="right">{tier.percent}%</TableCell>
                <TableCell align="right" title={`${tier.minutes} min`}>
                  {fmtDuration(tier.minutes)}
                </TableCell>
                <TableCell>
                  {report.overtimeStretches
                    .filter((stretch) => stretch.tier === tier.tier)
                    .map((stretch) => stretch.label)
                    .join(', ') || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {report.weeklyOvertimeRows.length > 0 && (
        <Table size="small" data-testid="weekly-overtime" sx={{ mt: 1, mb: 0.5, '& td, & th': { fontFamily: 'monospace', fontSize: 12, py: 0.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell>it_weekly_overtime tag</TableCell>
              <TableCell>tier</TableCell>
              <TableCell align="right">percent</TableCell>
              <TableCell align="right">minutes</TableCell>
              <TableCell>time ranges</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.weeklyOvertimeRows.map((row) => (
              <TableRow key={`${row.tag}:${row.tier}`}>
                <TableCell>{row.tag}</TableCell>
                <TableCell>{row.tier || '—'}</TableCell>
                <TableCell align="right">{fmtWeeklyOvertimePercent(row)}</TableCell>
                <TableCell align="right" title={`${row.minutes} min`}>
                  {fmtDuration(row.minutes)}
                </TableCell>
                <TableCell>
                  {report.weeklyOvertimeStretches
                    .filter((stretch) => stretch.tag === row.tag && stretch.tier === row.tier)
                    .map((stretch) => stretch.label)
                    .join(', ') || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {report.hoursBankDeposits.length > 0 && (
        <Table size="small" data-testid="hours-bank-deposits" sx={{ mt: 1, mb: 0.5, '& td, & th': { fontFamily: 'monospace', fontSize: 12, py: 0.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell>it_hours_bank deposit earned on</TableCell>
              <TableCell>expires on</TableCell>
              <TableCell align="right">minutes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.hoursBankDeposits.map((deposit, index) => (
              <TableRow key={`${deposit.earnedOn}:${index}`}>
                <TableCell>{deposit.earnedOn}</TableCell>
                <TableCell>{deposit.expiresOn}</TableCell>
                <TableCell align="right" title={`${deposit.minutes} min`}>
                  {fmtDuration(deposit.minutes)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Accordion disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 28, px: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Typography variant="caption" color="text.secondary">
            day outputs
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <JsonView value={report.other} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

/** One row per break type, the three *_break_by_types plugins side by side as day / night minutes. Break types are known only by uuid here. */
const BreakByTypesTable = ({ rows }: { rows: BreakTypeRow[] }) =>
  rows.length === 0 ? null : (
    <Table size="small" data-testid="break-by-types" sx={{ mt: 1, mb: 0.5, '& td, & th': { fontFamily: 'monospace', fontSize: 12, py: 0.25 } }}>
      <TableHead>
        <TableRow>
          <TableCell>break type</TableCell>
          {BREAK_BY_TYPES_PLUGINS.map((pluginId) => (
            <TableCell key={pluginId} align="right">
              {pluginId} (day / night)
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.breakTypeUuid}>
            <TableCell>{row.breakTypeUuid}</TableCell>
            {BREAK_BY_TYPES_PLUGINS.map((pluginId) => {
              const cell = row.byPlugin[pluginId];
              return (
                <TableCell key={pluginId} align="right">
                  {cell ? `${cell.day} / ${cell.night}` : '—'}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

/** One row per request attributed to the shift's day (attributed_requests): what a request-driven deduction, like the hours bank's request_minutes, is actually about. */
const AttributedRequestsTable = ({ rows }: { rows: AttributedRequestRow[] }) =>
  rows.length === 0 ? null : (
    <Table size="small" data-testid="attributed-requests" sx={{ mt: 1, mb: 0.5, '& td, & th': { fontFamily: 'monospace', fontSize: 12, py: 0.25 } }}>
      <TableHead>
        <TableRow>
          <TableCell>type</TableCell>
          <TableCell>subtype</TableCell>
          <TableCell>status</TableCell>
          <TableCell>from</TableCell>
          <TableCell>to</TableCell>
          <TableCell>hours calc</TableCell>
          <TableCell>all day</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((request) => (
          <TableRow key={request.uuid}>
            <TableCell>{request.requestType}</TableCell>
            <TableCell>{request.requestSubtypeName ?? '—'}</TableCell>
            <TableCell>{request.approvalStatus}</TableCell>
            <TableCell>{fmtRequestInstant(request.startedAt, request.timezone)}</TableCell>
            <TableCell>{fmtRequestInstant(request.endedAt, request.timezone)}</TableCell>
            <TableCell>{request.hoursCalculationType}</TableCell>
            <TableCell>{request.allDay ? 'yes' : 'no'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

const ShiftRow = ({ shift }: { shift: ShiftRun }) => {
  const report: ReportShift = buildReportShift(shift);
  if (report.error) return <Alert severity="error" sx={{ mb: 1 }}>{`${report.shiftId}: ${report.error}`}</Alert>;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {report.shiftId}
        </Typography>
        {report.scheduleName && <Typography variant="caption">{report.scheduleName}</Typography>}
        {report.working !== null && (
          <Typography variant="caption" color="text.secondary">
            {report.working ? 'working' : 'non-working'}
          </Typography>
        )}
        {report.chips.map((chip) => (
          <Typography key={chip.label} variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {chip.label} <b>{chip.value}</b>
          </Typography>
        ))}
      </Box>
      <ShiftTimeline
        expected={report.expected}
        actual={report.actual}
        markers={report.markers}
        tolerance={report.tolerance}
        ranges={report.ranges}
        snaps={report.snaps}
        actualWithAbsences={report.actualWithAbsences}
        toleranceWithAbsences={report.toleranceWithAbsences}
        actualRanges={report.actualRanges}
        actualRangesWithTolerance={report.actualRangesWithTolerance}
        absenceRanges={report.absenceRanges}
        nightRanges={report.nightRanges}
        nightRangesWithTolerance={report.nightRangesWithTolerance}
        earlyLateRanges={report.earlyLateRanges}
        restDayRanges={report.restDayRanges}
      />
      <MetricsRow metrics={report.metrics} />
      <BreakByTypesTable rows={report.breakByTypes} />
      <AttributedRequestsTable rows={report.attributedRequests} />
      <Accordion disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 28, px: 0, '& .MuiAccordionSummary-content': { my: 0 } }}>
          <Typography variant="caption" color="text.secondary">
            other outputs
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <JsonView value={report.other} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export const ReportView = ({ response, range }: { response: RecalculateResponse; range: DateRange }) => {
  const shiftsByDate = response.content.shifts.reduce<Record<string, ShiftRun[]>>((acc, s) => ({ ...acc, [s.date]: [...(acc[s.date] ?? []), s] }), {});
  const dayByDate = new Map((response.content.days ?? []).map((day) => [day.date, day]));
  const days = Array.from(new Set([...daysInRange(range), ...Object.keys(shiftsByDate), ...Array.from(dayByDate.keys())])).sort();
  const firstDayWithShifts = days.find((date) => (shiftsByDate[date] ?? []).length > 0);
  return (
    <Box>
      <SourcesStrip response={response} />
      {days.map((date) => {
        const shifts = shiftsByDate[date] ?? [];
        const day = dayByDate.get(date);
        const hasError = shifts.some((s) => s.error) || Boolean(day?.error);
        return (
          <Accordion key={date} data-testid="day-card" defaultExpanded={date === firstDayWithShifts || hasError} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{date}</Typography>
              <Typography sx={{ mx: 1.5, minWidth: '6.5em' }}>{weekdayName(date)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {shifts.length === 0 ? 'no shifts' : `${shifts.length} shift${shifts.length > 1 ? 's' : ''}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {shifts.map((shift, i) => (
                <ShiftRow key={`${shift.shiftId}-${i}`} shift={shift} />
              ))}
              {day && <DayRow day={day} />}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
